// Lightweight payment helpers — used by ProjectDialog & FinanceView.

export const PAYMENT_STATUS_OPTIONS = [
  { value: "planned",   label: "Geplant" },
  { value: "due",       label: "Fällig" },
  { value: "invoiced",  label: "Berechnet" },
  { value: "paid",      label: "Bezahlt" },
];

export const PAYMENT_STATUS_LABEL = Object.fromEntries(
  PAYMENT_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

export const DEFAULT_PAYMENT_LABELS = ["Anzahlung", "Zwischenzahlung", "Schlusszahlung"];

/* ---------------- Finance filter persistence ---------------- */

export const FINANCE_RANGE_KEY = "wallplanner_finance_range_v3";

// Anzeige des Zeitraums; leer = keine Begrenzung.
export function fmtRangeDE(fromDate, until) {
  if (!fromDate && !until) return "gesamt";
  if (fromDate && until) return `${fmtDateDE(fromDate)} – ${fmtDateDE(until)}`;
  return fromDate ? `ab ${fmtDateDE(fromDate)}` : `bis ${fmtDateDE(until)}`;
}
export const REVENUE_GOAL_KEY = "wallplanner_revenue_goal";
export const DEFAULT_REVENUE_GOAL = 48000;

export function loadRevenueGoal() {
  if (typeof window === "undefined") return DEFAULT_REVENUE_GOAL;
  const raw = window.localStorage.getItem(REVENUE_GOAL_KEY);
  if (raw == null || raw === "") return DEFAULT_REVENUE_GOAL;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_REVENUE_GOAL;
}

export function saveRevenueGoal(n) {
  if (typeof window === "undefined") return;
  const val = Number.isFinite(Number(n)) && Number(n) >= 0 ? Number(n) : DEFAULT_REVENUE_GOAL;
  try { window.localStorage.setItem(REVENUE_GOAL_KEY, String(val)); } catch { /* */ }
}

export function financeYearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

export function financeYearEnd() {
  return `${new Date().getFullYear()}-12-31`;
}

export function loadFinanceFilters() {
  // Start-Einstellung: Zeitraum ohne Datumsbegrenzung, Filter zurückgesetzt.
  const fb = {
    fromDate: "",
    until: "",
    upcomingStatus: "all",
    upcomingTypes: [],
  };
  if (typeof window === "undefined") return fb;
  try {
    const raw = window.localStorage.getItem(FINANCE_RANGE_KEY);
    if (!raw) return fb;
    const obj = JSON.parse(raw);
    const isIso = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
    return {
      fromDate: isIso(obj?.fromDate) ? obj.fromDate : "",
      until: isIso(obj?.until) ? obj.until : "",
      upcomingStatus: typeof obj?.upcomingStatus === "string" ? obj.upcomingStatus : fb.upcomingStatus,
      upcomingTypes: Array.isArray(obj?.upcomingTypes) ? obj.upcomingTypes : fb.upcomingTypes,
    };
  } catch {
    return fb;
  }
}

export function saveFinanceFilters(partial) {
  if (typeof window === "undefined") return;
  const cur = loadFinanceFilters();
  const next = { ...cur, ...partial };
  try {
    window.localStorage.setItem(FINANCE_RANGE_KEY, JSON.stringify(next));
  } catch { /* */ }
}

export function emptyPayment(label = "") {
  return { ref: newRef(), label, amount: "", invoice_date: "", due_date: "", paid_date: "", status: "planned" };
}

export function emptyServiceContract() {
  return { ref: newRef(), yearly_amount: "", invoice_date: "", due_date: "", paid_date: "", status: "planned", note: "" };
}

/** Add `n` days to a YYYY-MM-DD string. Returns new YYYY-MM-DD or "" on invalid. */
export function addDaysIso(iso, days) {
  if (!iso) return "";
  const parts = iso.slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return "";
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  if (isNaN(d)) return "";
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Currency formatter (EUR, locale de-DE). Pass numbers; null/undefined renders "—". */
const eurFmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
export function fmtEUR(value) {
  if (value == null || value === "" || isNaN(Number(value))) return "—";
  return eurFmt.format(Number(value));
}

/**
 * Normalize a payment row coming from the server (might have null fields)
 * to the controlled-form shape used by the dialog.
 */
export function newRef() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizePayment(p) {
  if (!p) return emptyPayment();
  return {
    ref: p.ref || newRef(),
    label: p.label ?? "",
    amount: p.amount == null ? "" : String(p.amount),
    invoice_date: p.invoice_date ?? "",
    due_date: p.due_date ?? "",
    paid_date: p.paid_date ?? "",
    status: p.status ?? "planned",
  };
}

export function normalizeServiceContract(sc) {
  if (!sc) return emptyServiceContract();
  return {
    ref: sc.ref || newRef(),
    yearly_amount: sc.yearly_amount == null ? "" : String(sc.yearly_amount),
    invoice_date: sc.invoice_date ?? "",
    due_date: sc.due_date ?? "",
    paid_date: sc.paid_date ?? "",
    status: sc.status ?? "planned",
    note: sc.note ?? "",
  };
}

/** Convert form payment → backend payload (null for empty / numeric for amount). */
export function paymentToPayload(p) {
  const hasAny =
    (p.label && p.label.trim()) ||
    (p.amount !== "" && p.amount != null) ||
    p.invoice_date || p.due_date || p.paid_date;
  if (!hasAny) return null;
  return {
    ref: p.ref || null,
    label: p.label?.trim() || null,
    amount: p.amount === "" || p.amount == null ? null : Number(p.amount),
    invoice_date: p.invoice_date || null,
    due_date: p.due_date || null,
    paid_date: p.paid_date || null,
    status: p.status || "planned",
  };
}

export function serviceContractToPayload(sc) {
  const note = sc.note?.trim() || "";
  const hasAny =
    (sc.yearly_amount !== "" && sc.yearly_amount != null) ||
    sc.invoice_date || sc.due_date || sc.paid_date || note;
  if (!hasAny) return null;
  return {
    ref: sc.ref || null,
    yearly_amount: sc.yearly_amount === "" || sc.yearly_amount == null ? null : Number(sc.yearly_amount),
    invoice_date: sc.invoice_date || null,
    due_date: sc.due_date || null,
    paid_date: sc.paid_date || null,
    status: sc.status || "planned",
    note: note || null,
  };
}

/** Today as YYYY-MM-DD in the user's local timezone. */
export function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Format a YYYY-MM-DD string as German date (DD.MM.YYYY). Returns "" on empty/invalid. */
export function fmtDateDE(iso) {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

const DE_WEEKDAYS = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"];

/** Format a YYYY-MM-DD string as "WD, DD.MM.YYYY" (timezone-safe). Returns "" on empty/invalid. */
export function fmtDateDEWeekday(iso) {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  if (!y || !m || !d) return s;
  const dow = new Date(Number(y), Number(m) - 1, Number(d)).getDay();
  return `${DE_WEEKDAYS[dow]}, ${d}.${m}.${y}`;
}

/** Stable payment-type keys used by the Finance view filter. */
export const PAYMENT_TYPE_OPTIONS = [
  { value: "deposit", label: "Anzahlung" },
  { value: "mid",     label: "Zwischenzahlung" },
  { value: "final",   label: "Schlusszahlung" },
  { value: "service", label: "Servicevertrag" },
  { value: "extra",   label: "Extras" },
];

function payTypeFor(kind, idx) {
  if (kind === "service") return "service";
  if (kind === "extra") return "extra";
  // kind === "payment"
  return ["deposit", "mid", "final"][idx] || "other";
}

/**
 * Derived display status: an "invoiced" entry whose due_date is today or earlier
 * is presented as "due". Internal storage is untouched.
 */
export function derivedDisplayStatus(entry, todayStr) {
  if (!entry) return "planned";
  const s = entry.status || "planned";
  if (s === "invoiced" && entry.due_date && entry.due_date <= todayStr) {
    return "due";
  }
  return s;
}

/**
 * Yield all payment rows across projects, including extra payments and the
 * service contract, as a flat list of entries ready for the Finance view.
 */
export function flattenFinanceEntries(projects) {
  const out = [];
  for (const p of projects) {
    // Archivierte Projekte: nur tatsächlich bezahlte Beträge zählen weiter.
    const archived = p.status === "archived";
    const skip = (pay) => !pay || (archived && pay.status !== "paid");
    const pushPay = (pay, idx, kind, fallbackLabel) => {
      if (skip(pay)) return;
      out.push({
        key: `${p.id}-${kind}${idx}`,
        project_id: p.id,
        project_name: p.name,
        client: p.client,
        slot: p.slot,
        kind,
        pay_type: payTypeFor(kind, idx),
        label: pay.label || fallbackLabel,
        amount: pay.amount == null ? null : Number(pay.amount),
        invoice_date: pay.invoice_date || null,
        due_date: pay.due_date || null,
        status: pay.status || "planned",
      });
    };
    (Array.isArray(p.payments) ? p.payments : []).forEach((pay, idx) =>
      pushPay(pay, idx, "payment", DEFAULT_PAYMENT_LABELS[idx] || `Zahlung ${idx + 1}`),
    );
    (Array.isArray(p.extra_payments) ? p.extra_payments : []).forEach((pay, idx) =>
      pushPay(pay, idx, "extra", `Zusatz ${idx + 1}`),
    );
    const sc = p.service_contract;
    if (sc && !skip(sc) && (sc.yearly_amount != null || sc.invoice_date || sc.due_date)) {
      out.push({
        key: `${p.id}-sc`,
        project_id: p.id,
        project_name: p.name,
        client: p.client,
        slot: p.slot,
        kind: "service",
        pay_type: "service",
        label: "Servicevertrag (jährlich)",
        amount: sc.yearly_amount == null ? null : Number(sc.yearly_amount),
        invoice_date: sc.invoice_date || null,
        due_date: sc.due_date || null,
        status: sc.status || "planned",
      });
    }
    (Array.isArray(p.service_contract_entries) ? p.service_contract_entries : []).forEach((pay, idx) => {
      if (skip(pay)) return;
      out.push({
        key: `${p.id}-sce${idx}`,
        project_id: p.id,
        project_name: p.name,
        client: p.client,
        slot: p.slot,
        kind: "service",
        pay_type: "service",
        label: pay.label || "Servicevertrag (jährlich)",
        amount: pay.amount == null ? null : Number(pay.amount),
        invoice_date: pay.invoice_date || null,
        due_date: pay.due_date || null,
        status: pay.status || "planned",
      });
    });
  }
  return out;
}
